import { useState, useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Pencil, Archive, AlertTriangle, Package, Paperclip, MapPin, Plus, Trash2, Check, X, Wrench, ShoppingCart, Printer, Layers, Copy, History } from "lucide-react";
import { PrintLabelDialog } from "@/components/parts/PrintLabelDialog";
import { useUpdatePart, useArchivePart, usePartVendors, useUpsertPartVendorNumber, usePartComponents, usePartUsedInAssemblies, useAddPartComponent, useUpdatePartComponent, useRemovePartComponent, useDuplicatePart } from "@/hooks/useParts";
import type { Part, PartComponent } from "@/hooks/useParts";
import { format } from "date-fns";
import type { Contact } from "@/hooks/useContacts";
import { useVendorContacts } from "@/hooks/useVendorContacts";
import { VendorSearchInput } from "@/components/shared/VendorSearchInput";
import { OrderPartDialog } from "@/components/parts/OrderPartDialog";
import {
  useAllZonesFlat, usePartZoneStock,
  useUpsertPartZoneStock, useDeletePartZoneStock,
  type PartZoneStock,
} from "@/hooks/useFacilityZones";
import { usePartAssets, useLinkPartAsset, useUnlinkPartAsset } from "@/hooks/usePartAssets";
import { useAssets } from "@/hooks/useAssets";
import { useParts } from "@/hooks/useParts";
import { Thumbnail } from "@/components/shared/Thumbnail";
import { ImageGallery } from "@/components/shared/ImageGallery";
import { FileAttachments } from "@/components/shared/FileAttachments";

// ── Stock by Zone ──────────────────────────────────────────────────────────

function StockByZone({ partId, orgId, unit }: { partId: string; orgId: string; unit: string | null }) {
  const stock = usePartZoneStock(partId);
  const zones = useAllZonesFlat();
  const upsert = useUpsertPartZoneStock();
  const remove = useDeletePartZoneStock();

  const [adding, setAdding] = useState(false);
  const [newZoneId, setNewZoneId] = useState("");
  const [newQty, setNewQty] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState("");
  const qtyInputRef = useRef<HTMLInputElement>(null);

  const usedZoneIds = new Set(stock.map((e) => e.zone_id));
  const availableZones = zones.filter((z) => !usedZoneIds.has(z.id));

  const handleAdd = () => {
    if (!newZoneId || !newQty) return;
    upsert.mutate({ part_id: partId, zone_id: newZoneId, quantity: parseFloat(newQty) || 0, org_id: orgId });
    setNewZoneId("");
    setNewQty("");
    setAdding(false);
  };

  const handleEditSave = (zoneId: string) => {
    upsert.mutate({ part_id: partId, zone_id: zoneId, quantity: parseFloat(editQty) || 0, org_id: orgId });
    setEditingId(null);
  };

  const total = stock.reduce((sum, e) => sum + e.quantity, 0);

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        Stock by Location
      </p>

      {stock.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No location stock tracked yet.
        </p>
      ) : (
        <div className="rounded-lg border overflow-hidden text-sm mb-2">
          {stock.map((row) => (
            <div key={row.zone_id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 group">
              <div className="flex-1 min-w-0">
                <span className="text-xs text-muted-foreground">{row.facility_code} › </span>
                <span className="font-medium">{row.zone_name}</span>
              </div>

              {editingId === row.zone_id ? (
                <div className="flex items-center gap-1">
                  <Input
                    ref={qtyInputRef}
                    type="number"
                    value={editQty}
                    onChange={(e) => setEditQty(e.target.value)}
                    className="w-20 h-7 text-sm text-right"
                    onKeyDown={(e) => { if (e.key === "Enter") handleEditSave(row.zone_id); if (e.key === "Escape") setEditingId(null); }}
                  />
                  <button onClick={() => handleEditSave(row.zone_id)} className="text-primary hover:text-primary/80">
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditingId(row.zone_id); setEditQty(row.quantity.toString()); }}
                    className="text-sm text-muted-foreground hover:text-foreground tabular-nums"
                  >
                    {row.quantity} <span className="text-xs">{unit}</span>
                  </button>
                  <button
                    onClick={() => remove.mutate({ partId, zoneId: row.zone_id })}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}

          {stock.length > 0 && (
            <div className="flex items-center justify-between px-3 py-2 bg-muted/50 font-medium text-sm">
              <span>Total</span>
              <span>{total} <span className="text-xs font-normal text-muted-foreground">{unit}</span></span>
            </div>
          )}
        </div>
      )}

      {!adding && (
        <button
          onClick={() => setAdding(true)}
          disabled={availableZones.length === 0}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 disabled:opacity-40"
        >
          <Plus className="h-3 w-3" />
          {availableZones.length === 0 ? "All locations assigned" : "Add location"}
        </button>
      )}

      {adding && (
        <div className="flex items-center gap-2 p-2 rounded-lg border bg-muted/30">
          <select
            value={newZoneId}
            onChange={(e) => setNewZoneId(e.target.value)}
            className="flex-1 text-sm bg-background border rounded-md px-2 py-1 h-8"
            autoFocus
          >
            <option value="">Select location…</option>
            {availableZones.map((z) => (
              <option key={z.id} value={z.id}>{z.label}</option>
            ))}
          </select>
          <Input
            type="number"
            placeholder="Qty"
            value={newQty}
            onChange={(e) => setNewQty(e.target.value)}
            className="w-20 h-8 text-sm text-right"
            onKeyDown={(e) => { if (e.key === "Enter") handleAdd(); if (e.key === "Escape") setAdding(false); }}
          />
          <button onClick={handleAdd} className="text-primary hover:text-primary/80">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={() => { setAdding(false); setNewZoneId(""); setNewQty(""); }} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Linked Assets ─────────────────────────────────────────────────────────

function LinkedAssets({ partId }: { partId: string }) {
  const { data: linked = [] } = usePartAssets(partId);
  const { data: allAssets = [] } = useAssets();
  const link = useLinkPartAsset();
  const unlink = useUnlinkPartAsset();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const linkedIds = new Set(linked.map((l) => l.asset_id));
  const available = allAssets.filter((a) => !linkedIds.has(a.id));
  // Build a name map for parent lookup in the dropdown
  const assetNameMap = Object.fromEntries(allAssets.map((a) => [a.id, a.name]));
  const assetLabel = (a: typeof allAssets[0]) =>
    a.parent_id ? `${assetNameMap[a.parent_id] ?? "?"} › ${a.name}` : a.name;
  const filtered = available.filter((a) =>
    assetLabel(a).toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (assetId: string) => {
    link.mutate({ partId, assetId }, {
      onSuccess: () => {
        // keep adding open so user can link more
        setSearch("");
        setShowDropdown(false);
        setTimeout(() => { searchRef.current?.focus(); setShowDropdown(true); }, 50);
      },
    });
  };

  const handleClose = () => { setAdding(false); setSearch(""); setShowDropdown(false); };

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <Wrench className="h-4 w-4 text-muted-foreground" />
        Assets ({linked.length})
      </p>

      {linked.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No assets linked yet.
        </p>
      ) : linked.length > 0 ? (
        <div className="rounded-lg border overflow-hidden mb-2">
          {linked.map((item) => (
            <div key={item.asset_id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 group">
              <Thumbnail url={item.image_url} size={72} className="h-7 w-7 rounded object-cover border border-border shrink-0" />
              <div className="flex-1 min-w-0">
                {item.parent_name && (
                  <p className="text-xs text-muted-foreground truncate">{item.parent_name} ›</p>
                )}
                <p className="text-sm font-medium truncate">{item.asset_name}</p>
                {item.asset_type && (
                  <p className="text-xs text-muted-foreground capitalize">{item.asset_type.replace("_", " ")}</p>
                )}
              </div>
              <button
                onClick={() => unlink.mutate({ partId, assetId: item.asset_id })}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {!adding && (
        <button
          onClick={() => { setAdding(true); setShowDropdown(true); }}
          disabled={available.length === 0}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 disabled:opacity-40"
        >
          <Plus className="h-3 w-3" />
          {available.length === 0 ? "All assets linked" : "Link asset"}
        </button>
      )}

      {adding && (
        <div className="rounded-lg border bg-muted/30 p-2 space-y-1">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={searchRef}
                autoFocus
                placeholder="Search assets…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                className="h-8 text-sm pr-2"
              />
              {showDropdown && (
                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">No assets found</p>
                  ) : (
                    filtered.map((a) => (
                      <button
                        key={a.id}
                        onMouseDown={(e) => { e.preventDefault(); handleSelect(a.id); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                      >
                        {a.parent_id ? (
                          <>
                            <span className="text-muted-foreground text-xs">{assetNameMap[a.parent_id] ?? ""} › </span>
                            {a.name}
                          </>
                        ) : a.name}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button onClick={handleClose} className="text-muted-foreground hover:text-foreground shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground px-1">Click an asset to link it. You can link multiple.</p>
        </div>
      )}
    </div>
  );
}

// ── Part Components ───────────────────────────────────────────────────────

function PartComponents({ partId }: { partId: string }) {
  const { data: components = [] } = usePartComponents(partId);
  const { data: usedIn = [] } = usePartUsedInAssemblies(partId);
  const { data: allParts = [] } = useParts();
  const add = useAddPartComponent();
  const update = useUpdatePartComponent();
  const remove = useRemovePartComponent();

  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [newQty, setNewQty] = useState("1");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const usedPartIds = new Set(components.map((c) => c.component_part_id));
  usedPartIds.add(partId);

  const available = allParts.filter((p) => !usedPartIds.has(p.id));
  const filtered = available.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.part_number ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = (p: Part) => {
    const qty = parseFloat(newQty) || 1;
    add.mutate({ assemblyPartId: partId, componentPartId: p.id, quantity: qty }, {
      onSuccess: () => { setSearch(""); setShowDropdown(false); setAdding(false); setNewQty("1"); },
    });
  };

  const handleEditSave = (comp: PartComponent) => {
    update.mutate({ id: comp.id, assemblyPartId: partId, quantity: parseFloat(editQty) || 1 });
    setEditingId(null);
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <Layers className="h-4 w-4 text-muted-foreground" />
        Components {components.length > 0 && `(${components.length})`}
      </p>

      {components.length === 0 && !adding ? (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No components defined yet.
        </p>
      ) : components.length > 0 ? (
        <div className="rounded-lg border overflow-hidden mb-2">
          {components.map((comp) => (
            <div key={comp.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 group">
              <Thumbnail url={comp.component_image_url} size={72} className="h-7 w-7 rounded object-cover border border-border shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{comp.component_name}</p>
                {comp.component_part_number && (
                  <p className="text-xs text-muted-foreground font-mono">{comp.component_part_number}</p>
                )}
              </div>

              {editingId === comp.id ? (
                <div className="flex items-center gap-1">
                  <Input
                    autoFocus
                    type="number"
                    value={editQty}
                    onChange={(e) => setEditQty(e.target.value)}
                    className="w-16 h-7 text-sm text-right"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleEditSave(comp);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                  <button onClick={() => handleEditSave(comp)} className="text-primary hover:opacity-80">
                    <Check className="h-4 w-4" />
                  </button>
                  <button onClick={() => setEditingId(null)} className="text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { setEditingId(comp.id); setEditQty(comp.quantity.toString()); }}
                    className="text-sm text-muted-foreground hover:text-foreground tabular-nums"
                    title="Click to edit quantity"
                  >
                    ×{comp.quantity}{comp.component_unit ? <span className="text-xs ml-0.5">{comp.component_unit}</span> : null}
                  </button>
                  <button
                    onClick={() => remove.mutate({ id: comp.id, assemblyPartId: partId })}
                    className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : null}

      {!adding && (
        <button
          onClick={() => { setAdding(true); setShowDropdown(true); }}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
        >
          <Plus className="h-3 w-3" />
          Add component
        </button>
      )}

      {adding && (
        <div className="rounded-lg border bg-muted/30 p-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={searchRef}
                autoFocus
                placeholder="Search parts…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                className="h-8 text-sm"
              />
              {showDropdown && (
                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                  {filtered.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-2">No parts found</p>
                  ) : (
                    filtered.map((p) => (
                      <button
                        key={p.id}
                        onMouseDown={(e) => { e.preventDefault(); handleSelect(p); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors"
                      >
                        <span className="font-medium">{p.name}</span>
                        {p.part_number && (
                          <span className="text-xs text-muted-foreground ml-2 font-mono">{p.part_number}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <Input
              type="number"
              min="0.01"
              step="any"
              value={newQty}
              onChange={(e) => setNewQty(e.target.value)}
              className="w-16 h-8 text-sm text-right shrink-0"
              placeholder="Qty"
            />
            <button
              onClick={() => { setAdding(false); setSearch(""); setNewQty("1"); }}
              className="text-muted-foreground hover:text-foreground shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground px-1">Set qty, then click a part to add it.</p>
        </div>
      )}

      {usedIn.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          Used in: {usedIn.map((u: any) => u.assembly.name).join(", ")}
        </p>
      )}
    </div>
  );
}

// ── Primary Vendor ────────────────────────────────────────────────────────

function PrimaryVendor({ part, vendors, onVendorChange }: {
  part: Part;
  vendors: Contact[];
  onVendorChange: (vendorId: string | null) => void;
}) {
  const [editingVpn, setEditingVpn] = useState(false);
  const [vpnValue, setVpnValue] = useState("");

  const { data: partVendors = [] } = usePartVendors(part.id);
  const upsertVendorNumber = useUpsertPartVendorNumber();

  const currentVendor = vendors.find((v) => v.id === part.vendor_id);

  // Existing vendor part # (if part_vendor record exists)
  const partVendorRecord = partVendors.find((pv: any) => pv.vendor_id === part.vendor_id);
  const existingVpn = partVendorRecord?.vendor_part_number ?? "";

  const handleVpnSave = () => {
    if (!part.vendor_id) return;
    upsertVendorNumber.mutate(
      { partId: part.id, vendorId: part.vendor_id, vendorPartNumber: vpnValue.trim() || null },
      { onSuccess: () => setEditingVpn(false) }
    );
  };

  return (
    <div>
      <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
        <ShoppingCart className="h-4 w-4 text-muted-foreground" />
        Primary Vendor
      </p>

      {currentVendor ? (
        <div className="rounded-lg border overflow-hidden mb-2">
          <div className="flex items-center gap-2 px-3 py-2 group">
            <span className="flex-1 text-sm font-medium">{currentVendor.company}</span>
            <button
              onClick={() => onVendorChange(null)}
              className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="border-t px-3 py-2">
            {editingVpn ? (
              <div className="flex items-center gap-1">
                <Input
                  autoFocus
                  value={vpnValue}
                  onChange={(e) => setVpnValue(e.target.value)}
                  placeholder="Vendor part #"
                  className="h-7 text-xs font-mono flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") handleVpnSave(); if (e.key === "Escape") setEditingVpn(false); }}
                />
                <button onClick={handleVpnSave} className="text-primary hover:opacity-80"><Check className="h-3.5 w-3.5" /></button>
                <button onClick={() => setEditingVpn(false)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <button
                className="flex items-center gap-1.5 group/vpn w-full text-left"
                onClick={() => { setVpnValue(existingVpn); setEditingVpn(true); }}
              >
                <span className="text-xs text-muted-foreground">Vendor Part #:</span>
                <span className="text-xs font-mono">
                  {existingVpn || <span className="text-muted-foreground italic">click to add</span>}
                </span>
                <Pencil className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover/vpn:opacity-100 ml-auto" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground py-2 px-3 rounded-lg bg-muted/50 mb-2">
          No vendor linked yet.
        </p>
      )}

      <VendorSearchInput
        vendors={vendors}
        onSelect={(v) => onVendorChange(v.id)}
        placeholder={currentVendor ? "Change vendor..." : "Search vendors..."}
        className="h-9 text-sm"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────

interface PartDetailSheetProps {
  part: Part | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPartUpdated?: (part: Part) => void;
}

export function PartDetailSheet({ part: partProp, open, onOpenChange, onPartUpdated }: PartDetailSheetProps) {
  const [displayPart, setDisplayPart] = useState<Part | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const [unitCost, setUnitCost] = useState("");
  const [qtyInStock, setQtyInStock] = useState("");
  const [minQuantity, setMinQuantity] = useState("");
  const [reorderQuantity, setReorderQuantity] = useState("");
  const [unit, setUnit] = useState("");
  const [notes, setNotes] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const updatePart = useUpdatePart();
  const archivePart = useArchivePart();
  const duplicatePart = useDuplicatePart();
  const { data: vendors = [] } = useVendorContacts();

  useEffect(() => {
    setDisplayPart(partProp);
    setIsEditing(false);
  }, [partProp]);

  const handleEdit = () => {
    if (!displayPart) return;
    setName(displayPart.name);
    setDescription(displayPart.description || "");
    setPartNumber(displayPart.part_number || "");
    setUnitCost(displayPart.unit_cost?.toString() || "");
    setQtyInStock(displayPart.qty_in_stock.toString());
    setMinQuantity(displayPart.min_quantity.toString());
    setReorderQuantity(displayPart.reorder_quantity?.toString() || "");
    setUnit(displayPart.unit || "each");
    setNotes(displayPart.notes || "");
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!displayPart) return;
    updatePart.mutate(
      {
        id: displayPart.id,
        input: {
          name,
          description: description || null,
          part_number: partNumber || null,
          unit_cost: unitCost ? parseFloat(unitCost) : null,
          qty_in_stock: parseFloat(qtyInStock) || 0,
          min_quantity: parseFloat(minQuantity) || 0,
          reorder_quantity: reorderQuantity ? parseFloat(reorderQuantity) : null,
          unit: unit || "each",
          notes: notes || null,
        },
      },
      {
        onSuccess: (data) => {
          setDisplayPart(data);
          onPartUpdated?.(data);
          setIsEditing(false);
        },
      }
    );
  };

  const handleArchive = () => {
    if (!displayPart) return;
    archivePart.mutate(displayPart.id, { onSuccess: () => onOpenChange(false) });
  };

  if (!displayPart) return null;

  const isLowStock = displayPart.qty_in_stock <= displayPart.min_quantity && displayPart.min_quantity > 0;
  const qtyAvailable = displayPart.qty_in_stock - displayPart.qty_reserved;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-xl leading-tight">{displayPart.name}</SheetTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {displayPart.part_number && <Badge variant="outline" className="font-mono">{displayPart.part_number}</Badge>}
              {isLowStock && (
                <Badge variant="destructive" className="gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Low Stock
                </Badge>
              )}
              {!isEditing && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1" onClick={handleEdit}>
                    <Pencil className="h-3 w-3" />Edit
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 px-2.5 text-xs gap-1" onClick={() => setLabelOpen(true)}>
                    <Printer className="h-3 w-3" />Label
                  </Button>
                </div>
              )}
            </div>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {!isEditing ? (
              <>
                <ImageGallery
                  entityType="part"
                  entityId={displayPart.id}
                  orgId={displayPart.org_id}
                />

                {displayPart.description && (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{displayPart.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">In Stock</p>
                    <p className="text-lg font-semibold">{displayPart.qty_in_stock} <span className="text-sm font-normal text-muted-foreground">{displayPart.unit}</span></p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Available</p>
                    <p className="text-lg font-semibold">{qtyAvailable} <span className="text-sm font-normal text-muted-foreground">{displayPart.unit}</span></p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Min Quantity</p>
                    <p className="text-lg font-semibold">{displayPart.min_quantity}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Reorder Qty</p>
                    <p className="text-lg font-semibold">{displayPart.reorder_quantity ?? "—"}</p>
                  </div>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Unit Cost</p>
                    <p className="text-lg font-semibold">{displayPart.unit_cost != null ? `$${displayPart.unit_cost.toFixed(2)}` : "—"}</p>
                  </div>
                </div>

                {displayPart.qty_reserved > 0 && (
                  <div className="flex items-center gap-2 text-sm">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>{displayPart.qty_reserved} reserved, {displayPart.qty_ordered} on order</span>
                  </div>
                )}

                <Button className="w-full gap-1.5" onClick={() => setOrderOpen(true)}>
                  <ShoppingCart className="h-4 w-4" />Purchase Order
                </Button>

                {/* Stock by Location */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <StockByZone partId={displayPart.id} orgId={displayPart.org_id} unit={displayPart.unit} />
                </div>

                {/* Linked Assets */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <LinkedAssets partId={displayPart.id} />
                </div>

                {/* Components */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <PartComponents partId={displayPart.id} />
                </div>

                {/* Primary Vendor */}
                <div className="rounded-lg border border-gray-300 bg-gray-100 p-3">
                  <PrimaryVendor part={displayPart} vendors={vendors} onVendorChange={(vid) => {
                    updatePart.mutate({ id: displayPart.id, input: { vendor_id: vid } }, {
                      onSuccess: (data) => { setDisplayPart(data); onPartUpdated?.(data); },
                    });
                  }} />
                </div>

                {displayPart.notes && (
                  <div className="rounded-lg bg-muted/50 px-3 py-2.5">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{displayPart.notes}</p>
                  </div>
                )}

                <Separator />

                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    Files & Manuals
                  </p>
                  <FileAttachments
                    entityType="part"
                    entityId={displayPart.id}
                    orgId={displayPart.org_id}
                  />
                </div>

                {/* Traceability */}
                {(displayPart.updated_by_name || displayPart.updated_at) && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
                    <History className="h-3 w-3 shrink-0" />
                    <span>
                      Last updated
                      {displayPart.updated_by_name ? ` by ${displayPart.updated_by_name}` : ""}
                      {displayPart.updated_at
                        ? ` · ${format(new Date(displayPart.updated_at), "MMM d, yyyy")}`
                        : ""}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground gap-1.5"
                    onClick={() => duplicatePart.mutate(displayPart)}
                    disabled={duplicatePart.isPending}
                  >
                    <Copy className="h-3.5 w-3.5" />New from this
                  </Button>
                  <button
                    onClick={() => setShowArchiveDialog(true)}
                    className="text-xs text-muted-foreground hover:text-destructive transition-colors px-2 py-1"
                  >
                    Archive
                  </button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>OEM Part Number</Label>
                    <Input value={partNumber} onChange={(e) => setPartNumber(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit Cost ($)</Label>
                    <Input type="number" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Qty In Stock</Label>
                    <Input type="number" value={qtyInStock} onChange={(e) => setQtyInStock(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Unit</Label>
                    <Input value={unit} onChange={(e) => setUnit(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Min Quantity</Label>
                    <Input type="number" value={minQuantity} onChange={(e) => setMinQuantity(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Reorder Qty</Label>
                    <Input type="number" placeholder="e.g. 25" value={reorderQuantity} onChange={(e) => setReorderQuantity(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                  <Button onClick={handleSave} disabled={updatePart.isPending}>
                    {updatePart.isPending ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {displayPart && (
        <OrderPartDialog
          part={displayPart}
          open={orderOpen}
          onOpenChange={setOrderOpen}
        />
      )}

      {displayPart && (
        <PrintLabelDialog
          part={displayPart}
          open={labelOpen}
          onOpenChange={setLabelOpen}
        />
      )}

      <AlertDialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {displayPart.name}?</AlertDialogTitle>
            <AlertDialogDescription>This part will be hidden from active inventory views.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive}>Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
